import { useState, useCallback } from 'react';

/**
 * Hook for triggering ConfirmModal imperatively, like window.confirm()
 * but async/Promise-based.
 *
 * Usage:
 *   const { confirm, modalProps } = useConfirm();
 *   ...
 *   const ok = await confirm({
 *     title: 'Delete project?',
 *     message: 'This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     destructive: true,
 *   });
 *   if (ok) await doDelete();
 *   ...
 *   <ConfirmModal {...modalProps} />
 */
const CLOSED = {
  isOpen: false,
  title: '',
  message: '',
  detail: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  confirmNote: '',
  alternateLabel: '',
  destructive: false,
  choice: false,
  resolver: null,
};

export function useConfirm() {
  const [state, setState] = useState(CLOSED);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        ...CLOSED,
        isOpen: true,
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        destructive: !!opts.destructive,
        choice: false,
        resolver: resolve,
      });
    });
  }, []);

  // Resolves 'safe', 'danger', or null. Safe is the default choice.
  const choose = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        ...CLOSED,
        isOpen: true,
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        detail: opts.detail || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        confirmNote: opts.confirmNote || '',
        alternateLabel: opts.alternateLabel || '',
        destructive: false,
        choice: true,
        resolver: resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState((s) => {
      s.resolver?.(s.choice ? 'safe' : true);
      return { ...s, isOpen: false, resolver: null };
    });
  }, []);

  const handleAlternate = useCallback(() => {
    setState((s) => {
      s.resolver?.('danger');
      return { ...s, isOpen: false, resolver: null };
    });
  }, []);

  const handleCancel = useCallback(() => {
    setState((s) => {
      s.resolver?.(s.choice ? null : false);
      return { ...s, isOpen: false, resolver: null };
    });
  }, []);

  return {
    confirm,
    choose,
    modalProps: {
      isOpen: state.isOpen,
      title: state.title,
      message: state.message,
      detail: state.detail,
      confirmLabel: state.confirmLabel,
      cancelLabel: state.cancelLabel,
      confirmNote: state.confirmNote,
      alternateLabel: state.alternateLabel,
      destructive: state.destructive,
      onConfirm: handleConfirm,
      onAlternate: handleAlternate,
      onCancel: handleCancel,
    },
  };
}
